import React, { useState, useEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import ScrollToTop from './components/ScrollToTop';
import './App.css';
import { BACKEND_URL, API as API_URL } from './config/apiConfig';

// Core Components (loaded immediately)
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import Sidebar from './components/SidebarCollapsible';

// Lazy-loaded Components (loaded on demand for better performance)
const InventoryDashboard = lazy(() => import('./components/InventoryDashboard'));
const ItemMaster = lazy(() => import('./components/ItemMaster'));
const BOMManagement = lazy(() => import('./components/BOMManagement'));
const InventoryTransactions = lazy(() => import('./components/InventoryTransactions'));
const ProjectManagement = lazy(() => import('./components/ProjectManagement'));
const LocationManagement = lazy(() => import('./components/LocationManagement'));
const ItemLocationManagement = lazy(() => import('./components/ItemLocationManagement'));
const SupplierManagement = lazy(() => import('./components/SupplierManagement'));
const PurchaseOrderManagement = lazy(() => import('./components/PurchaseOrderManagement'));
const PurchaseReceiving = lazy(() => import('./components/PurchaseReceiving'));
const MRBManagement = lazy(() => import('./components/MRBManagement'));
const QCInspectionPlans = lazy(() => import('./components/QCInspectionPlans'));
const QCDashboard = lazy(() => import('./components/QCDashboard'));
const PutAwayTasks = lazy(() => import('./components/PutAwayTasks'));
const TransactionHistory = lazy(() => import('./components/TransactionHistory'));
const MenuConfiguration = lazy(() => import('./components/MenuConfiguration'));
const CustomerManagement = lazy(() => import('./components/CustomerManagement'));
const SalesOrderManagement = lazy(() => import('./components/SalesOrderManagement'));
const OrderFulfillment = lazy(() => import('./components/OrderFulfillment'));
const PlannerCodeManagement = lazy(() => import('./components/PlannerCodeManagement'));
const MRPDashboard = lazy(() => import('./components/MRPDashboard'));
const MRPScheduleManagement = lazy(() => import('./components/MRPScheduleManagement'));
const ChartOfAccounts = lazy(() => import('./components/ChartOfAccounts'));
const ExecutiveDashboard = lazy(() => import('./components/ExecutiveDashboard'));
const PlannerDashboard = lazy(() => import('./components/PlannerDashboard'));
const BuyerDashboard = lazy(() => import('./components/BuyerDashboard'));
const FinanceDashboard = lazy(() => import('./components/FinanceDashboard'));
const CashManagement = lazy(() => import('./components/CashManagement'));
const FinancialReporting = lazy(() => import('./components/FinancialReporting'));
const StandardCostManagement = lazy(() => import('./components/StandardCostManagement'));
const WorkCenterCostManagement = lazy(() => import('./components/WorkCenterCostManagement'));
const CostVarianceDashboard = lazy(() => import('./components/CostVarianceDashboard'));
const AccountsReceivable = lazy(() => import('./components/AccountsReceivable'));
const QualityDashboard = lazy(() => import('./components/QualityDashboard'));
const QCPlanManagement = lazy(() => import('./components/QCPlanManagement'));
const CostCenterManagement = lazy(() => import('./components/CostCenterManagement'));
const WorkOrderTypeManagement = lazy(() => import('./components/WorkOrderTypeManagement'));
const WorkCenterManagement = lazy(() => import('./components/WorkCenterManagement'));
const OperationManagement = lazy(() => import('./components/OperationManagement'));
const RoutingManagement = lazy(() => import('./components/RoutingManagement'));
const WorkOrderManagement = lazy(() => import('./components/WorkOrderManagement'));
const RoleManagement = lazy(() => import('./components/RoleManagement'));
const UserRoleAssignment = lazy(() => import('./components/UserRoleAssignment'));
const MasterAdminDashboard = lazy(() => import('./components/MasterAdminDashboard'));
const AdminMigrations = lazy(() => import('./components/AdminMigrations'));
const AdminVarianceCorrection = lazy(() => import('./components/AdminVarianceCorrection'));
const SubinventoryManagement = lazy(() => import('./components/SubinventoryManagement'));
const InventoryLocationManager = lazy(() => import('./components/InventoryLocationManager'));
const InventoryByLocation = lazy(() => import('./components/InventoryByLocation'));
const SiteManagement = lazy(() => import('./components/SiteManagement'));
const UserGuide = lazy(() => import('./components/UserGuide'));
const SaaSCustomerManagement = lazy(() => import('./components/SaaSCustomerManagement'));
const CustomerOnboarding = lazy(() => import('./components/CustomerOnboarding'));
const SaaSMetricsDashboard = lazy(() => import('./components/SaaSMetricsDashboard'));
const BackupManagement = lazy(() => import('./components/BackupManagement'));
const AIAssistant = lazy(() => import('./components/AIAssistant'));
const EmailDebugPage = lazy(() => import('./components/EmailDebugPage'));
const WorkOrderPrint = lazy(() => import('./components/WorkOrderPrint'));
const WorkOrderMaterialIssue = lazy(() => import('./components/WorkOrderMaterialIssue'));
const ShopFloorExecution = lazy(() => import('./components/ShopFloorExecution'));
const ProductionDashboard = lazy(() => import('./components/ProductionDashboard'));
const ShopFloorScheduling = lazy(() => import('./components/ShopFloorScheduling'));
const InspectionManagement = lazy(() => import('./components/InspectionManagement'));
const NonConformanceManagement = lazy(() => import('./components/NonConformanceManagement'));
const InventoryAdjustments = lazy(() => import('./components/InventoryAdjustments'));
const Fulfillment = lazy(() => import('./components/Fulfillment'));
const WMS = lazy(() => import('./components/WMS'));
const APSDashboard = lazy(() => import('./components/aps/APSDashboard'));
const RMAManagement = lazy(() => import('./components/RMAManagement'));
const COAManagement = lazy(() => import('./components/COAManagement'));
const InspectionCheckpoints = lazy(() => import('./components/InspectionCheckpoints'));
const CrossValidationRules = lazy(() => import('./components/CrossValidationRules'));
const TransferOrderManagement = lazy(() => import('./components/TransferOrderManagement'));
const E2ETestingDashboard = lazy(() => import('./components/E2ETestingDashboard'));

const SupplierInvoices = lazy(() => import('./components/SupplierInvoices'));
const APDashboard = lazy(() => import('./components/APDashboard'));
const FixedAssetManagement = lazy(() => import('./components/FixedAssetManagement'));

// Marketing Components (Public)
const HomePage = lazy(() => import('./components/marketing/HomePage'));
const FeaturesPage = lazy(() => import('./components/marketing/FeaturesPage'));
const SolutionsPage = lazy(() => import('./components/marketing/SolutionsPage'));
const DemoRequestPage = lazy(() => import('./components/marketing/DemoRequestPage'));
const AboutPage = lazy(() => import('./components/marketing/AboutPage'));
const ContactPage = lazy(() => import('./components/marketing/ContactPage'));
const UserGuidePage = lazy(() => import('./components/marketing/UserGuidePage'));
const GettingStartedGuide = lazy(() => import('./components/marketing/guides/GettingStartedGuide'));
const AIAssistantGuide = lazy(() => import('./components/marketing/guides/AIAssistantGuide'));
const AIPlanningGuide = lazy(() => import('./components/marketing/guides/AIPlanningGuide'));
const PurchaseWorkflowGuide = lazy(() => import('./components/marketing/guides/PurchaseWorkflowGuide'));
const ManufacturingWorkflowGuide = lazy(() => import('./components/marketing/guides/ManufacturingWorkflowGuide'));
const SalesWorkflowGuide = lazy(() => import('./components/marketing/guides/SalesWorkflowGuide'));
const MRPPlanningGuide = lazy(() => import('./components/marketing/guides/MRPPlanningGuide'));
const FinanceModuleGuide = lazy(() => import('./components/marketing/guides/FinanceModuleGuide'));
const REMIPage = lazy(() => import('./components/marketing/REMIPage'));
const ERPHomePage = lazy(() => import('./components/marketing/ERPHomePage'));
const CISuiteHomePage = lazy(() => import('./components/ci-suite/CISuiteHomePage'));
const CISuiteFeaturesPage = lazy(() => import('./components/ci-suite/CISuiteFeaturesPage'));
const CISuiteREMIPage = lazy(() => import('./components/ci-suite/CISuiteREMIPage'));
const CISuitePricingPage = lazy(() => import('./components/ci-suite/CISuitePricingPage'));

// Loading component
const LoadingFallback = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
      <p className="text-gray-600">Loading...</p>
    </div>
  </div>
);

// Use dynamic API configuration
const API = API_URL;

// Auth Context
const AuthContext = React.createContext();

export const useAuth = () => {
  const context = React.useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      if (token) {
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        
        // Try to get stored user data
        const storedUser = localStorage.getItem('user');
        if (storedUser) {
          try {
            setUser(JSON.parse(storedUser));
          } catch (e) {
            console.error('Error parsing stored user data:', e);
            localStorage.removeItem('user');
          }
        }
      }
      setLoading(false);
    };

    // Add axios interceptor for handling 401 errors
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          // Token is invalid, clear auth data
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          setToken(null);
          setUser(null);
          delete axios.defaults.headers.common['Authorization'];
        }
        return Promise.reject(error);
      }
    );

    initAuth();

    // Cleanup interceptor on unmount
    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, [token]);

  const login = (token, user) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(user));
    setToken(token);
    setUser(user);
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setToken(null);
    setUser(null);
    delete axios.defaults.headers.common['Authorization'];
  };

  return (
    <AuthContext.Provider value={{ user, token, login, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

const ProtectedRoute = ({ children }) => {
  const { token, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-pulse text-lg">Loading...</div>
      </div>
    );
  }

  return token ? children : <Navigate to="/login" replace />;
};

const AppLayout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(true);

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar isOpen={sidebarOpen} setIsOpen={setSidebarOpen} />
      <div className="flex-1 flex flex-col overflow-hidden">
        <header className="bg-white shadow-sm border-b border-gray-200 px-6 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="p-2 rounded-md hover:bg-gray-100 transition-colors"
              data-testid="sidebar-toggle"
            >
              <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
            <div className="flex items-center space-x-3">
              <img 
                src="/ff-logo.png" 
                alt="FF Logo" 
                className="h-8 w-auto"
              />
              <div>
                <p className="text-sm text-gray-600">Comprehensive Enterprise Resource Planning</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <AIAssistant />
              <UserProfile />
            </div>
          </div>
        </header>
        <main className="flex-1 overflow-auto p-6">
          {children}
        </main>
      </div>
    </div>
  );
};

const UserProfile = () => {
  const { user, logout } = useAuth();
  const [dropdownOpen, setDropdownOpen] = useState(false);

  return (
    <div className="relative">
      <button
        onClick={() => setDropdownOpen(!dropdownOpen)}
        className="flex items-center space-x-2 p-2 rounded-md hover:bg-gray-100 transition-colors"
        data-testid="user-profile-button"
      >
        <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white font-semibold">
          {user?.full_name?.charAt(0) || 'U'}
        </div>
        <span className="text-sm text-gray-700">{user?.full_name || 'User'}</span>
        <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      
      {dropdownOpen && (
        <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg border border-gray-200 z-50">
          <div className="py-1">
            <div className="px-4 py-2 text-sm text-gray-500 border-b border-gray-100">
              <div className="font-medium">{user?.full_name}</div>
              <div className="text-xs">{user?.role}</div>
            </div>
            <button
              onClick={logout}
              className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
              data-testid="logout-button"
            >
              Sign out
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <Router>
          <ScrollToTop />
          <Suspense fallback={<LoadingFallback />}>
            <Routes>
            {/* Public Marketing Routes */}
            <Route path="/" element={<HomePage />} />
            <Route path="/features" element={<FeaturesPage />} />
            <Route path="/solutions" element={<SolutionsPage />} />
            <Route path="/demo" element={<DemoRequestPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/guide" element={<UserGuidePage />} />
            <Route path="/guide/getting-started" element={<GettingStartedGuide />} />
            <Route path="/guide/ai-assistant" element={<AIAssistantGuide />} />
            <Route path="/guide/ai-planning" element={<AIPlanningGuide />} />
            <Route path="/guide/purchase-workflow" element={<PurchaseWorkflowGuide />} />
            <Route path="/guide/manufacturing-workflow" element={<ManufacturingWorkflowGuide />} />
            <Route path="/guide/sales-workflow" element={<SalesWorkflowGuide />} />
            <Route path="/guide/mrp-planning" element={<MRPPlanningGuide />} />
            <Route path="/guide/finance-module" element={<FinanceModuleGuide />} />
            <Route path="/remi" element={<REMIPage />} />
            <Route path="/erp" element={<ERPHomePage />} />
            <Route path="/ci-suite" element={<CISuiteHomePage />} />
            <Route path="/ci-suite/features" element={<CISuiteFeaturesPage />} />
            <Route path="/ci-suite/remi" element={<CISuiteREMIPage />} />
            <Route path="/ci-suite/pricing" element={<CISuitePricingPage />} />
            
            {/* Login Route */}
            <Route path="/login" element={<Login />} />
            
            {/* Protected ERP Application Routes */}
            <Route path="/app" element={
              <ProtectedRoute>
                <AppLayout>
                  <Dashboard />
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/dashboard" element={
              <ProtectedRoute>
                <AppLayout>
                  <Dashboard />
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/inventory-dashboard" element={
              <ProtectedRoute>
                <AppLayout>
                  <InventoryDashboard />
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/items" element={
              <ProtectedRoute>
                <AppLayout>
                  <ItemMaster />
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/bom" element={
              <ProtectedRoute>
                <AppLayout>
                  <BOMManagement />
                </AppLayout>
              </ProtectedRoute>
            } />
            {/* Redirect /inventory to /transaction-history */}
            <Route path="/inventory" element={<Navigate to="/transaction-history" replace />} />
            <Route path="/projects" element={
              <ProtectedRoute>
                <AppLayout>
                  <ProjectManagement />
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/locations" element={
              <ProtectedRoute>
                <AppLayout>
                  <LocationManagement />
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/item-locations" element={
              <ProtectedRoute>
                <AppLayout>
                  <ItemLocationManagement />
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/suppliers" element={
              <ProtectedRoute>
                <AppLayout>
                  <SupplierManagement />
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/purchase-orders" element={
              <ProtectedRoute>
                <AppLayout>
                  <PurchaseOrderManagement />
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/purchase-receiving" element={
              <ProtectedRoute>
                <AppLayout>
                  <PurchaseReceiving />
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/mrb" element={
              <ProtectedRoute>
                <AppLayout>
                  <MRBManagement />
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/qc-inspection-plans" element={
              <ProtectedRoute>
                <AppLayout>
                  <QCInspectionPlans />
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/qc-dashboard" element={
              <ProtectedRoute>
                <AppLayout>
                  <QCDashboard />
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/put-away-tasks" element={
              <ProtectedRoute>
                <AppLayout>
                  <PutAwayTasks />
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/transaction-history" element={
              <ProtectedRoute>
                <AppLayout>
                  <TransactionHistory />
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/menu-configuration" element={
              <ProtectedRoute>
                <AppLayout>
                  <MenuConfiguration />
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/customers" element={
              <ProtectedRoute>
                <AppLayout>
                  <CustomerManagement />
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/sales-orders" element={
              <ProtectedRoute>
                <AppLayout>
                  <SalesOrderManagement />
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/order-fulfillment" element={
              <ProtectedRoute>
                <AppLayout>
                  <OrderFulfillment />
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/planner-codes" element={
              <ProtectedRoute>
                <AppLayout>
                  <PlannerCodeManagement />
                </AppLayout>
              </ProtectedRoute>
            } />
            {/* Redirect /mrp to /mrp-dashboard for convenience */}
            <Route path="/mrp" element={<Navigate to="/mrp-dashboard" replace />} />
            <Route path="/mrp-dashboard" element={
              <ProtectedRoute>
                <AppLayout>
                  <MRPDashboard />
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/mrp-scheduling" element={
              <ProtectedRoute>
                <AppLayout>
                  <MRPScheduleManagement />
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/executive-dashboard" element={
              <ProtectedRoute>
                <AppLayout>
                  <ExecutiveDashboard />
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/planner-dashboard" element={
              <ProtectedRoute>
                <AppLayout>
                  <PlannerDashboard />
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/buyer-dashboard" element={
              <ProtectedRoute>
                <AppLayout>
                  <BuyerDashboard />
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/chart-of-accounts" element={
              <ProtectedRoute>
                <AppLayout>
                  <ChartOfAccounts />
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/finance-dashboard" element={
              <ProtectedRoute>
                <AppLayout>
                  <FinanceDashboard />
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/cash-management" element={
              <ProtectedRoute>
                <AppLayout>
                  <CashManagement />
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/financial-reporting" element={
              <ProtectedRoute>
                <AppLayout>
                  <FinancialReporting />
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/standard-costs" element={
              <ProtectedRoute>
                <AppLayout>
                  <StandardCostManagement />
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/work-center-costs" element={
              <ProtectedRoute>
                <AppLayout>
                  <WorkCenterCostManagement />
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/cost-variances" element={
              <ProtectedRoute>
                <AppLayout>
                  <CostVarianceDashboard />
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/accounts-receivable" element={
              <ProtectedRoute>
                <AppLayout>
                  <AccountsReceivable />
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/quality-dashboard" element={
              <ProtectedRoute>
                <AppLayout>
                  <QualityDashboard />
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/inspections" element={
              <ProtectedRoute>
                <AppLayout>
                  <InspectionManagement />
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/non-conformances" element={
              <ProtectedRoute>
                <AppLayout>
                  <NonConformanceManagement />
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/qc-plan-management" element={
              <ProtectedRoute>
                <AppLayout>
                  <QCPlanManagement />
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/cost-centers" element={
              <ProtectedRoute>
                <AppLayout>
                  <CostCenterManagement />
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/work-order-types" element={
              <ProtectedRoute>
                <AppLayout>
                  <WorkOrderTypeManagement />
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/work-centers" element={
              <ProtectedRoute>
                <AppLayout>
                  <WorkCenterManagement />
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/operations" element={
              <ProtectedRoute>
                <AppLayout>
                  <OperationManagement />
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/routings" element={
              <ProtectedRoute>
                <AppLayout>
                  <RoutingManagement />
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/work-orders" element={
              <ProtectedRoute>
                <AppLayout>
                  <WorkOrderManagement />
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/shop-floor" element={
              <ProtectedRoute>
                <AppLayout>
                  <ShopFloorExecution />
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/shop-floor-scheduling" element={
              <ProtectedRoute>
                <AppLayout>
                  <ShopFloorScheduling />
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/production-dashboard" element={
              <ProtectedRoute>
                <AppLayout>
                  <ProductionDashboard />
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/roles" element={
              <ProtectedRoute>
                <AppLayout>
                  <RoleManagement />
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/user-assignments" element={
              <ProtectedRoute>
                <AppLayout>
                  <UserRoleAssignment />
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/master-admin" element={
              <ProtectedRoute>
                <AppLayout>
                  <MasterAdminDashboard />
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/admin-migrations" element={
              <ProtectedRoute>
                <AppLayout>
                  <AdminMigrations />
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/admin-variance-correction" element={
              <ProtectedRoute>
                <AppLayout>
                  <AdminVarianceCorrection />
                </AppLayout>
              </ProtectedRoute>
            } />

            <Route path="/saas-customers" element={
              <ProtectedRoute>
                <AppLayout>
                  <SaaSCustomerManagement />
                </AppLayout>
              </ProtectedRoute>
            } />

            <Route path="/transfers" element={
              <ProtectedRoute>
                <AppLayout>
                  <TransferOrderManagement />
                </AppLayout>
              </ProtectedRoute>
            } />

            <Route path="/e2e-testing" element={
              <ProtectedRoute>
                <AppLayout>
                  <E2ETestingDashboard />
                </AppLayout>
              </ProtectedRoute>
            } />

            <Route path="/customer-onboarding" element={
              <ProtectedRoute>
                <AppLayout>
                  <CustomerOnboarding />
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/saas-metrics" element={
              <ProtectedRoute>
                <AppLayout>
                  <SaaSMetricsDashboard />
                </AppLayout>
              </ProtectedRoute>
            } />

            <Route path="/backups" element={
              <ProtectedRoute>
                <AppLayout>
                  <BackupManagement />
                </AppLayout>
              </ProtectedRoute>
            } />

            <Route path="/subinventories" element={
              <ProtectedRoute>
                <AppLayout>
                  <SubinventoryManagement />
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/inventory-location-manager" element={
              <ProtectedRoute>
                <AppLayout>
                  <InventoryLocationManager />
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/inventory-by-location" element={
              <ProtectedRoute>
                <AppLayout>
                  <InventoryByLocation />
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/sites" element={
              <ProtectedRoute>
                <AppLayout>
                  <SiteManagement />
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/user-guide" element={
              <ProtectedRoute>
                <AppLayout>
                  <UserGuide />
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/email-debug" element={
              <ProtectedRoute>
                <AppLayout>
                  <EmailDebugPage />
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/work-orders/:workOrderId/print" element={
              <ProtectedRoute>
                <WorkOrderPrint />
              </ProtectedRoute>
            } />
            <Route path="/work-orders/:workOrderId/materials" element={
              <ProtectedRoute>
                <AppLayout>
                  <WorkOrderMaterialIssue />
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/inventory-adjustments" element={
              <ProtectedRoute>
                <AppLayout>
                  <InventoryAdjustments />
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/fulfillment" element={
              <ProtectedRoute>
                <AppLayout>
                  <Fulfillment />
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/rma" element={
              <ProtectedRoute>
                <AppLayout>
                  <RMAManagement />
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/wms" element={
              <ProtectedRoute>
                <AppLayout>
                  <WMS />
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/aps-dashboard" element={
              <ProtectedRoute>
                <AppLayout>
                  <APSDashboard />
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/coa-management" element={
              <ProtectedRoute>
                <AppLayout>
                  <COAManagement />
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/inspection-checkpoints" element={
              <ProtectedRoute>
                <AppLayout>
                  <InspectionCheckpoints />
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/cross-validation-rules" element={
              <ProtectedRoute>
                <AppLayout>
                  <CrossValidationRules />
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/supplier-invoices" element={
              <ProtectedRoute>
                <AppLayout>
                  <SupplierInvoices />
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/ap-dashboard" element={
              <ProtectedRoute>
                <AppLayout>
                  <APDashboard />
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/fixed-assets" element={
              <ProtectedRoute>
                <AppLayout>
                  <FixedAssetManagement />
                </AppLayout>
              </ProtectedRoute>
            } />
            </Routes>
          </Suspense>
        </Router>
      </AuthProvider>
    </div>
  );
}

export default App;
